/**
 * Iter 87 — Selector encadenado Equipo → Modelo.
 *
 * Sustituye a los antiguos `EditableCatalogSelect` (Equipo, Iter 71) y
 * `ModelSelect` (Modelo, Iter 83).
 *
 * Reglas:
 *   - El desplegable "Equipo" muestra sólo familias activas.
 *   - El desplegable "Modelo" muestra sólo modelos activos de la familia
 *     seleccionada. Si no hay familia seleccionada → mostrar sólo el
 *     placeholder ("Seleccionar modelo") y quedar deshabilitado.
 *   - Compatibilidad legado: si `deviceType` o `model` corresponden a
 *     un valor no presente/activo en el catálogo (reparación antigua),
 *     se muestran como opción visible tal cual, sin marca.
 *   - Cambiar de familia limpia el modelo (evita combinaciones inválidas).
 *   - Botón "Gestionar" arriba a la derecha lleva al usuario a
 *     Configuración > Catálogo de dispositivos.
 */
import { Link } from "react-router-dom";
import { Settings2 } from "lucide-react";

export default function DeviceModelSelect({
  deviceType, model, onDeviceChange, onModelChange, catalog = [], required = false,
}) {
  const activeFamilies = catalog.filter((f) => f.active);
  const currentFamily = catalog.find(
    (f) => f.name?.trim().toLowerCase() === (deviceType || "").trim().toLowerCase(),
  );
  const activeModels = ((currentFamily?.models) || []).filter((m) => m.active);

  // Compatibilidad legado
  const legacyFamily =
    deviceType && !activeFamilies.some((f) => f.name === deviceType) ? deviceType : "";
  const legacyModel =
    model && !activeModels.some((m) => m.name === model) ? model : "";

  const handleFamilyChange = (v) => {
    onDeviceChange(v);
    // Si el modelo actual no está en la nueva familia, se limpia.
    const newFamily = catalog.find(
      (f) => f.name?.trim().toLowerCase() === v.trim().toLowerCase(),
    );
    const newActiveModels = (newFamily?.models || []).filter((m) => m.active);
    if (model && !newActiveModels.some((m) => m.name === model)) {
      onModelChange("");
    }
  };

  return (
    <>
      {/* EQUIPO */}
      <div>
        <div className="flex items-center justify-between">
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            Equipo {required && <span className="text-red-600">*</span>}
          </label>
          <Link
            to="/settings?tab=devices"
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#525252] hover:text-[#6E8B3D]"
            data-testid="repair-device-manage"
            title="Gestionar catálogo de dispositivos"
          >
            <Settings2 size={12} /> Gestionar
          </Link>
        </div>
        <select
          value={deviceType || ""}
          onChange={(e) => handleFamilyChange(e.target.value)}
          data-testid="repair-device"
          className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
        >
          <option value="">Seleccionar equipo</option>
          {legacyFamily && <option value={legacyFamily}>{legacyFamily}</option>}
          {activeFamilies.map((f) => (
            <option key={f.id} value={f.name}>{f.name}</option>
          ))}
        </select>
      </div>

      {/* MODELO */}
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Modelo
        </label>
        <select
          value={model || ""}
          onChange={(e) => onModelChange(e.target.value)}
          data-testid="repair-model"
          disabled={!deviceType && !legacyFamily}
          className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none disabled:bg-[#F7F7F4] disabled:text-[#A8A8A0]"
        >
          <option value="">
            {deviceType || legacyFamily ? "Seleccionar modelo" : "Selecciona primero un equipo"}
          </option>
          {legacyModel && <option value={legacyModel}>{legacyModel}</option>}
          {activeModels.map((m) => (
            <option key={m.id} value={m.name}>{m.name}</option>
          ))}
        </select>
      </div>
    </>
  );
}
